
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isHomePage = location.pathname === "/" || location.pathname === "";

  const navItems = [
    ...(isHomePage ? [] : [{ name: "Home", href: "/", type: "page" }]),
    { name: "Services", href: "/services", type: "page" },
    { name: "About", href: "/about", type: "page" },
    { name: "Portfolio", href: "/portfolio", type: "page" },
    { name: "Contact", href: "/contact", type: "page" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-zinc-950/80 backdrop-blur-xl border-b border-white/5 py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="group flex items-center gap-2"
          >
            <div className="relative">
              <span className="text-2xl font-bold tracking-tighter text-white group-hover:text-gradient-gold transition-all duration-300">
                FortifyTek
              </span>
              <div className="absolute -bottom-1 left-0 w-0 h-px bg-gradient-to-r from-amber-500 to-transparent group-hover:w-full transition-all duration-300" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="relative px-4 py-2 text-sm font-medium text-zinc-400 hover:text-white transition-colors duration-300 group"
              >
                {item.name}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-px bg-amber-500 group-hover:w-1/2 transition-all duration-300" />
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button
              className="btn-primary rounded-full px-6 h-10 text-sm font-semibold"
              onClick={() => navigate('/contact')}
            >
              Start Project
            </Button>
          </div>

          {/* Mobile Navigation Toggle */}
          <button
            className="md:hidden relative w-10 h-10 flex items-center justify-center text-white"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
          >
            <div className="relative w-6 h-6">
              <span
                className={`absolute left-0 w-6 h-0.5 bg-current transition-all duration-300 ${
                  isOpen ? "top-3 rotate-45" : "top-1"
                }`}
              />
              <span
                className={`absolute left-0 top-3 w-6 h-0.5 bg-current transition-all duration-300 ${
                  isOpen ? "opacity-0" : "opacity-100"
                }`}
              />
              <span
                className={`absolute left-0 w-6 h-0.5 bg-current transition-all duration-300 ${
                  isOpen ? "top-3 -rotate-45" : "top-5"
                }`}
              />
            </div>
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          id="mobile-navigation"
          className={`md:hidden fixed inset-x-0 top-[72px] bg-zinc-950/98 backdrop-blur-xl border-b border-white/5 transition-all duration-500 ${
            isOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4 pointer-events-none"
          }`}
        >
          <div className="container mx-auto px-6 py-8">
            <div className="flex flex-col gap-2">
              {navItems.map((item, index) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="group flex items-center justify-between py-4 border-b border-white/5 last:border-0"
                  onClick={() => setIsOpen(false)}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <span className="text-2xl font-semibold text-white group-hover:text-amber-400 transition-colors">
                    {item.name}
                  </span>
                  <svg
                    className="w-5 h-5 text-zinc-600 group-hover:text-amber-400 group-hover:translate-x-1 transition-all"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              ))}
            </div>

            <div className="mt-8">
              <Button
                className="w-full btn-primary rounded-full py-6 text-lg font-semibold"
                onClick={() => {
                  navigate('/contact');
                  setIsOpen(false);
                }}
              >
                Start Project
              </Button>
            </div>

            {/* Mobile Contact Info */}
            <div className="mt-8 pt-8 border-t border-white/5">
              <p className="label-mono text-zinc-500 mb-3">Get in touch</p>
              <a href="mailto:ftsupport@fortifytek.com" className="text-zinc-400 hover:text-amber-400 transition-colors">
                ftsupport@fortifytek.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};
